import 'amazon-connect-streams';
import 'amazon-connect-chatjs';
import { useEffect, useRef } from 'react';
import { useChatContext } from '../../../contexts/ChatContext';
import { ContactTypingInfo } from '../../../contexts/types';
import { resolveImageAttachmentUrls } from '../../../utils/chatAttachmentUrls';
import { getChatSession } from '../../../utils/getChatSession';
import { ParticipantRole } from '../../../utils/participantRole';

interface UseChatContactHandlersProps {
  chatNotificationAudio: HTMLAudioElement;
  selectedChatRef: React.MutableRefObject<string | null>;
}

export function useChatContactHandlers({ chatNotificationAudio, selectedChatRef }: UseChatContactHandlersProps): void {
  const { setChatMessages, setUnreadMessages, setIsTyping } = useChatContext();
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    connect.contact(contact => {
      if (contact.getType() !== connect.ContactType.CHAT) {
        return;
      }

      contact.onConnected(async () => {
        const chatSession = await getChatSession(contact);
        if (!chatSession) {
          return;
        }

        // Messages are appended in arrival order; an image shows up once its URL has been fetched.
        const showImagesWhenResolved = (attachments: connect.AttachmentItem[] | undefined): void => {
          void resolveImageAttachmentUrls(chatSession, attachments).then(hasNewUrls => {
            if (hasNewUrls) setChatMessages(prevChatMessages => [...prevChatMessages]);
          });
        };

        chatSession.onMessage(async (event: connect.ChatMessageEvent) => {
          const message = event.data as connect.ChatTranscriptItem;
          setChatMessages(prevChatMessages => [...prevChatMessages, message]);
          showImagesWhenResolved(message.Attachments);

          if (message.ParticipantRole === ParticipantRole.CUSTOMER && contact.contactId !== selectedChatRef.current) {
            setUnreadMessages(prev => ({
              ...prev,
              [contact.contactId]: (prev[contact.contactId] || 0) + 1
            }));
          }

          if (document.hidden) {
            void chatNotificationAudio.play();
          }
        });

        chatSession.onTyping(async (event: connect.ChatTypingEvent) => {
          if (event.data.ParticipantRole === ParticipantRole.CUSTOMER) {
            const newTypingEvent: ContactTypingInfo = {
              contactId: contact.contactId,
              isTyping: true
            };

            setIsTyping(prevTypingEvents => {
              const filteredEvents = prevTypingEvents.filter(item => item.contactId !== contact.contactId);
              return [...filteredEvents, newTypingEvent];
            });

            setTimeout(() => {
              setIsTyping(prevTypingEvents => {
                return prevTypingEvents.filter(item => item.contactId !== contact.contactId);
              });
            }, 12000);
          }
        });

        const sessionTranscript = await chatSession.getTranscript({});
        const transcriptResponse = sessionTranscript.data?.Transcript;

        if (transcriptResponse) {
          setChatMessages(prevTranscript => [...prevTranscript, ...transcriptResponse]);
          transcriptResponse.forEach(item => showImagesWhenResolved(item.Attachments));
        }
      });
    });
  }, [chatNotificationAudio, selectedChatRef, setChatMessages, setUnreadMessages, setIsTyping]);
}
